/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// utils
// material
import { Button, displayModal, displayErrorMessage } from '@insticator/insticator-ui';
import Space from 'material/space/space'
// content
// styles
import injectSheet from "react-jss";
import styles from './controlbarmodal_styles';
import PreviewConfig from 'material/previewconfig/previewconfig';
import { ColorPicker, InlineMessage, SingleSelectDropDown, Switch } from '@insticator/insticator-ui/dist/material';
import { ReactComponent as CommentIcon } from 'assets/icons/product-layout/comment.svg'
import { ReactComponent as TrendingNowIcon } from 'assets/icons/product-layout/trending-now.svg'
import { ReactComponent as ArticleSentimentIcon } from 'assets/icons/product-layout/article-sentiment.svg'
import { createThemeColors, getHueFromHex } from 'utils/theme';
import ToggleButton from 'material/togglebutton/togglebutton';
import { updateControlBarConfig } from 'state/thunks/productmanager';


const placementMap = {
  RIGHT_EDGE: {value: 'RIGHT_EDGE', name: 'Right edge'},
  LEFT_EDGE: {value: 'LEFT_EDGE', name: 'Left edge'},
  BOTTOM: {value: 'BOTTOM', name: 'Bottom'}
}

const placementOptions = [placementMap.RIGHT_EDGE, placementMap.LEFT_EDGE, placementMap.BOTTOM]

const alignmentMap = {
  TOP: {value: 'TOP', name: 'Top'},
  MIDDLE: {value: 'MIDDLE', name: 'Middle'},
  BOTTOM: {value: 'BOTTOM', name: 'Bottom'},
  LEFT: {value: 'LEFT', name: 'Left'},
  RIGHT: {value: 'RIGHT', name: 'Right'},
  CENTER: {value: 'CENTER', name: 'Center'}
}

const leftRightEdgesAlignmentOptions = [alignmentMap.TOP, alignmentMap.MIDDLE, alignmentMap.BOTTOM]
const bottomAlignmentOptions = [alignmentMap.LEFT, alignmentMap.RIGHT, alignmentMap.CENTER]


/* ========== ~~~~~~~~~~ CONTROL BAR MODAL ~~~~~~~~~~ ========== */
const ControlBarModal  = injectSheet(styles)((props) => {
  const dispatch = useDispatch();

  const { siteUUID } = useSelector(state => state.session);

  const { tempProductManagerConfig } = useSelector(state => state.productManager)

  const [device, selectDevice] = useState('desktop')

  const desktopControlBarConfig = tempProductManagerConfig.controlBarConfig?.draft?.desktop
  const mobileControlBarConfig = tempProductManagerConfig.controlBarConfig?.draft?.mobile


  const { products, showCommenting, showTrendingNow, showArticleSentiment, onCancel, onSave } = props

  const [isProcessing, setIsProcessing] = useState(false)

  const [tempAlignment, setTempAlignment] = useState({
    desktop: desktopControlBarConfig?.alignment || 'TOP',
    mobile: mobileControlBarConfig?.alignment || 'TOP'
  })
  const [tempPlacement, setTempPlacement] = useState({
    desktop: desktopControlBarConfig?.placement || 'RIGHT_EDGE',
    mobile: mobileControlBarConfig?.placement || 'RIGHT_EDGE'
  })

  const [isMonochrome, setIsMonochrome] = useState({
    desktop: desktopControlBarConfig?.enableMonochrome || false,
    mobile: mobileControlBarConfig?.enableMonochrome || false
  })
  const [customPrimaryColor, ] = useState({
    desktop: desktopControlBarConfig?.primaryColor || '#061240',
    mobile: mobileControlBarConfig?.primaryColor || '#061240'
  })
  const [tempPrimaryColor, setTempPrimaryColor] = useState({
    desktop: desktopControlBarConfig?.primaryColor || '#061240',
    mobile: mobileControlBarConfig?.primaryColor || '#061240'
  })
  const [selectedAlignmentOptions, selectAlignmentOptions] = useState({
    desktop: desktopControlBarConfig?.placement === 'BOTTOM' ? bottomAlignmentOptions : leftRightEdgesAlignmentOptions,
    mobile: mobileControlBarConfig?.placement === 'BOTTOM' ? bottomAlignmentOptions : leftRightEdgesAlignmentOptions
  })


  const [colorSchemaValid, setColorSchemaValid] = useState({
     customPrimaryColor: true
})
  const handleCancel = () => {
    dispatch(displayModal(false))
    onCancel?.()
  }

  const handleChange = (property,  value) => {
    if(property === 'alignment') { 
      setTempAlignment({
      ...tempAlignment,
      [device]: value
    })
  }
    if(property === 'placement') {
      setTempPlacement({
        ...tempPlacement,
        [device]: value
      })

      // set the correct alignment options if we changed to another Placement
      selectAlignmentOptions({
        ...selectedAlignmentOptions,
        [device]: value === 'BOTTOM' ? bottomAlignmentOptions : leftRightEdgesAlignmentOptions
      })

      // Set the correct alignment if we changed to another Placement
      let alignment = tempAlignment[device]
      if(value === 'BOTTOM') {
        // if placement is BOTTOM
        if(['TOP', 'MIDDLE', 'BOTTOM'].includes(tempAlignment[device])) alignment = 'CENTER'
      } else {
        // else if placement is LEFT_EDGE or RIGHT_EDGE
        if(['LEFT', 'RIGHT', 'CENTER'].includes(tempAlignment[device])) alignment = 'TOP'
      }
      setTempAlignment({
        ...tempAlignment,
        [device]: alignment
      })
    }
  }

  const handleSaveControlBarConfig = useCallback(async () => {
    setIsProcessing(true)
    let response;

    const controlBarConfigPayload = {
      mobile: {
        uuid: mobileControlBarConfig?.uuid || null,
        primaryColor: tempPrimaryColor.mobile,
        enableMonochrome: isMonochrome.mobile,
        alignment: tempAlignment.mobile,
        placement: tempPlacement.mobile,
      },
      desktop: {
        uuid: desktopControlBarConfig?.uuid || null,
        primaryColor: tempPrimaryColor.desktop,
        enableMonochrome: isMonochrome.desktop,
        alignment: tempAlignment.desktop,
        placement: tempPlacement.desktop,
      }
    }
    const payload = { controlBarConfig: {...controlBarConfigPayload}, siteId: siteUUID}

    onSave?.(controlBarConfigPayload)
    setIsProcessing(false)
  }, [
      desktopControlBarConfig?.uuid,
      mobileControlBarConfig?.uuid, 
      tempPrimaryColor,
      isMonochrome,
      tempAlignment,
      tempPlacement, 
      siteUUID,
      onSave
  ])


  const handleMonochromeChange = (isMonoColor) => {
    setIsMonochrome({
      ...isMonochrome,
      [device]: isMonoColor
    })
  }


  const handlePrimaryColorChange =  (color) => {
    setTempPrimaryColor({
      ...tempPrimaryColor,
      [device]: color
    })
  }

  const validateColor = (isValid) => {
    setColorSchemaValid({
      ...colorSchemaValid,
      'customPrimaryColor': isValid
    })
  }

  const resetColor = () => {
    setTempPrimaryColor({
      ...customPrimaryColor
    })
  }

  const themeColors = useMemo(() => createThemeColors(false, getHueFromHex(tempPrimaryColor[device]), !!isMonochrome[device]), [tempPrimaryColor, device, isMonochrome])

  return (
    <div className={'controlbarmodal'}>
      <h4>Control Bar Configurations</h4>
      <ToggleButton selected={device} items={[{value: 'desktop', label: 'Desktop'}, {value: 'mobile', label: 'Mobile'}]} onChange={(item) => selectDevice?.(item.value)} />
      <Space gap={24} direction={'row'}>
      <Space gap={24} direction={'column'}> 
        <div className={`primarycolor disabled-${!!isMonochrome[device]}`}>
          <ColorPicker 
            valid={true}
            color={tempPrimaryColor[device]}
            savedColor={tempPrimaryColor[device]}
            label={'Primary Color'}
            handleColorChange={(color, e) => handlePrimaryColorChange(color)}
            processColorUpdate={(color, e) => handlePrimaryColorChange(color)}
            validateColor={(isValid, e) => validateColor(isValid, 'customPrimaryColor')}
            handleReset={() => resetColor('customPrimaryColor')} 
          />
          <Space klass={'usemonochrome'} gap={8} alignItems={'center'}>
            <Switch 
              on={!!(isMonochrome[device])} 
              handleClick={() => handleMonochromeChange(!isMonochrome[device])} 
            />
            <span>Monochrome</span>
          </Space>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <SingleSelectDropDown 
            name={placementMap[tempPlacement[device]].name} 
            searchable={false} 
            onSelect={opt =>  handleChange('placement', opt.value)} 
            items={placementOptions}
            label={'Placement'}
          />
        </div>
        <SingleSelectDropDown 
          name={alignmentMap[tempAlignment[device]].name} 
          searchable={false} 
          onSelect={opt =>  handleChange('alignment', opt.value)} 
          items={selectedAlignmentOptions[device]}
          label={'Alignment'}
          updateOnChangeOfItems={true}
        />
      </Space>
      <PreviewConfig contentklass={`${tempPlacement[device]}_PLACEMENT ${tempAlignment[device]}`} >
        <div className={`controlbarpreview ${tempPlacement[device]}_PLACEMENT ${tempAlignment[device]}`} style={{background: themeColors['backgroundPrimary'], color: themeColors['primary']}}>
          <Space gap={16} direction={tempPlacement[device] === 'BOTTOM' ? 'row' : 'column'} justifyContent={'center'} alignItems={'center'} style={{height: '100%'}}>
          {products.filter(product => {
          if(product === 'COMMENTING_V2' && showCommenting) return product
          if(product === 'TRENDING_NOW_V2' && showTrendingNow) return product
          if(product === 'ARTICLE_SENTIMENT_V2' && showArticleSentiment) return product
        }).map(product => (
          <>
            {product === 'COMMENTING_V2' && showCommenting && <Space klass={'icons'} style={{ borderBottom: `1px solid ${themeColors['primary']}`}}><CommentIcon width={18} height={18} />128</Space>}
            {product === 'TRENDING_NOW_V2' && showTrendingNow && <Space klass={'icons'} style={{ borderBottom: `1px solid ${themeColors['primary']}`}}><TrendingNowIcon width={22} height={22} /></Space>}
            {product === 'ARTICLE_SENTIMENT_V2' && showArticleSentiment && <Space klass={'icons'} style={{ borderBottom: `1px solid ${themeColors['primary']}`}}><ArticleSentimentIcon width={18} height={18} />128</Space>}
          </>
        ))}
          </Space>
        </div>
      </PreviewConfig>
    </Space>
      <InlineMessage 
        key={'controlbarnote'}
        display={true}
        name={'controlbarnote'}
        variant={'transparent'}
        icon={'exclamation-circle'}
        message={
        <span>
          These configurations are applied to any product group with the <strong>Control Bar</strong> trigger. 
          Only one control bar will display on your site. 
        </span>
        }
        close={false}
      />
      <Space style={{marginTop: '32px'}} alignItems={'center'}>
        <Button text='Cancel' variant='none' color='light' disabled={isProcessing} handleClick={handleCancel} />
        <Button 
          text='Save' 
          variant='primary' 
          processingText={'Saving Configurations'} 
          processing={isProcessing} 
          handleClick={() => handleSaveControlBarConfig()} 
        />
      </Space>
    </div>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default ControlBarModal